import React, { useState, useRef, useEffect } from 'react';
import { useT } from '../BuilderApp.jsx';
import WizardShell from '../WizardShell.jsx';

const FONTS = [
  'Inter',
  'Playfair Display',
  'Roboto',
  'Poppins',
  'DM Sans',
  'Space Grotesk'
];

function StepNextHint({ step }) {
  const t = useT();
  const msg = t[`next${step}`];

  if (!msg) return null;

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '.4rem',
        justifyContent: 'center',
        marginTop: '.55rem',
        fontSize: '.72rem',
        color: 'rgba(148,163,184,.55)',
        letterSpacing: '.01em',
        lineHeight: 1.4
      }}
    >
      <svg
        width="11"
        height="11"
        viewBox="0 0 11 11"
        fill="none"
        style={{ flexShrink: 0, opacity: .55 }}
      >
        <path
          d="M2 5.5h7M6 2.5l3 3-3 3"
          stroke="currentColor"
          strokeWidth="1.4"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>

      <span>{msg}</span>
    </div>
  );
}

export default function Step3({
  config,
  setField,
  next,
  back,
  lang,
  setLang
}) {
  const t = useT();

  const upd = (k, v) => {
    setField('branding', {
      ...config.branding,
      [k]: v
    });
  };

  const ok =
    config.branding.company_name.trim().length >= 2;

  const [dragLogo, setDragLogo] = useState(false);
  const [showUrlField, setShowUrlField] = useState(false);

  const logoFileRef = useRef(null);

  /*
   * Local preview URL.
   *
   * This is NOT uploaded to the server.
   * It only allows the browser to display the selected File.
   */
  const [logoPreview, setLogoPreview] = useState(
    config.branding.logo_dataUrl ||
    config.branding.logo_url ||
    null
  );

  /*
   * Create local preview from the actual File object.
   */

  useEffect(() => {
    console.log('logo_file changed:', config.branding.logo_file);
  }, [config.branding.logo_file]);
  const handleLogoFile = (file) => {
    if (!file) return;

    // Make sure it is an image
    if (!file.type.match(/^image\//)) {
      alert('Please select an image file.');
      return;
    }

    // 4 MB max
    if (file.size > 4 * 1024 * 1024) {
      alert('Logo must be smaller than 4 MB.');
      return;
    }

    /*
     * IMPORTANT:
     * Store the actual File object.
     *
     * This File will later be available as:
     *
     * config.branding.logo_file
     *
     * and can be appended to FormData.
     */
    const previewUrl = URL.createObjectURL(file);

    setLogoPreview(previewUrl);

    setField('branding', {
      ...config.branding,
      logo_file: file,
      logo_dataUrl: null,
      logo_url: ''
    });
  };

  /*
   * Cleanup the temporary object URL.
   */
  useEffect(() => {
    return () => {
      if (
        logoPreview &&
        logoPreview.startsWith('blob:')
      ) {
        URL.revokeObjectURL(logoPreview);
      }
    };
  }, [logoPreview]);

  /*
   * Remove logo.
   */
  const removeLogo = () => {
    if (
      logoPreview &&
      logoPreview.startsWith('blob:')
    ) {
      URL.revokeObjectURL(logoPreview);
    }

    setLogoPreview(null);

    upd('logo_file', null);
    upd('logo_dataUrl', null);
    upd('logo_url', '');
  };

  /*
   * Existing URL logo can still be used if needed.
   */
  const handleLogoUrl = (value) => {
    upd('logo_url', value);

    if (value) {
      upd('logo_file', null);
      upd('logo_dataUrl', null);
      setLogoPreview(value);
    } else {
      setLogoPreview(null);
    }
  };

  return (
    <WizardShell
      step={3}
      total={8}
      title={t.s3_title}
      subtitle={t.s3_sub}
      onBack={back}
      lang={lang}
      setLang={setLang}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '1.25rem'
        }}
      >

        {/* Business name */}
        <div>
          <label className="field-label">
            {t.s3_name}
          </label>

          <input
            type="text"
            className="field-input"
            placeholder="Bella's Bistro"
            value={config.branding.company_name}
            onChange={e =>
              upd('company_name', e.target.value)
            }
            style={{ fontSize: '1rem' }}
          />
        </div>

        {/* Logo upload */}
        <div>
          <label className="field-label">
            {t.s3_logo_upload}{' '}
            <span
              style={{
                fontWeight: 400,
                textTransform: 'none',
                letterSpacing: 'normal',
                color: 'var(--dim)'
              }}
            >
              {t.s3_opt}
            </span>
          </label>

          {/* Hidden file input */}
          <input
            ref={logoFileRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={e => {
              const file = e.target.files?.[0];

              if (file) {
                handleLogoFile(file);
              }

              /*
               * Reset input so selecting the same file
               * again will trigger onChange.
               */
              e.target.value = '';
            }}
          />

          {logoPreview ? (
            /*
             * Logo selected / URL provided
             */
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '1rem',
                background: 'rgba(255,255,255,.04)',
                border: '1.5px solid var(--border2)',
                borderRadius: 12,
                padding: '.9rem 1rem'
              }}
            >
              <img
                src={logoPreview}
                alt="logo"
                style={{
                  height: 40,
                  maxWidth: 120,
                  objectFit: 'contain',
                  borderRadius: 4
                }}
              />

              <div style={{ flex: 1 }}>
                <div
                  style={{
                    fontSize: '.78rem',
                    fontWeight: 600,
                    color: 'var(--text)',
                    marginBottom: '.2rem'
                  }}
                >
                  {config.branding.logo_file
                    ? (
                      lang === 'no'
                        ? 'Logo lastet opp ✓'
                        : 'Logo selected ✓'
                    )
                    : (
                      lang === 'no'
                        ? 'Logo-URL satt ✓'
                        : 'Logo URL set ✓'
                    )}
                </div>

                {/* Show selected filename */}
                {config.branding.logo_file && (
                  <div
                    style={{
                      fontSize: '.68rem',
                      color: 'var(--dim)',
                      marginBottom: '.35rem',
                      wordBreak: 'break-all'
                    }}
                  >
                    {config.branding.logo_file.name}
                  </div>
                )}

                <button
                  type="button"
                  onClick={removeLogo}
                  style={{
                    fontSize: '.72rem',
                    color: 'var(--red)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: 0,
                    fontFamily: 'inherit'
                  }}
                >
                  {lang === 'no' ? 'Fjern' : 'Remove'}
                </button>
              </div>

              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() =>
                  logoFileRef.current?.click()
                }
                style={{
                  flexShrink: 0,
                  fontSize: '.72rem'
                }}
              >
                {lang === 'no' ? 'Bytt' : 'Change'}
              </button>
            </div>
          ) : (
            /*
             * Drop zone
             */
            <div
              onClick={() =>
                logoFileRef.current?.click()
              }

              onDragOver={e => {
                e.preventDefault();
                setDragLogo(true);
              }}

              onDragLeave={() =>
                setDragLogo(false)
              }

              onDrop={e => {
                e.preventDefault();
                setDragLogo(false);

                const file =
                  e.dataTransfer.files?.[0];

                if (file) {
                  handleLogoFile(file);
                }
              }}

              style={{
                border: `2px dashed ${dragLogo
                  ? 'var(--blue)'
                  : 'rgba(255,255,255,.18)'
                  }`,
                borderRadius: 14,
                padding: '1.5rem 1rem',
                textAlign: 'center',
                cursor: 'pointer',
                background: dragLogo
                  ? 'rgba(59,130,246,.06)'
                  : 'rgba(255,255,255,.02)',
                transition: 'all .2s'
              }}
            >
              <div
                style={{
                  fontSize: '1.6rem',
                  marginBottom: '.5rem'
                }}
              >
                🖼️
              </div>

              <div
                style={{
                  fontSize: '.82rem',
                  fontWeight: 600,
                  color: 'var(--text)',
                  marginBottom: '.25rem'
                }}
              >
                {t.s3_logo_upload}
              </div>

              <div
                style={{
                  fontSize: '.72rem',
                  color: 'var(--dim)'
                }}
              >
                {t.s3_logo_upload_hint}
              </div>
            </div>
          )}

          {/* URL toggle */}
          <button
            type="button"
            onClick={() =>
              setShowUrlField(v => !v)
            }
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '.3rem',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: 'var(--dim)',
              fontSize: '.72rem',
              fontFamily: 'inherit',
              marginTop: '.5rem',
              padding: '.15rem 0'
            }}
          >
            <span style={{ fontSize: '.65rem' }}>
              {showUrlField ? '▾' : '▸'}
            </span>

            {t.s3_logo_url_toggle}
          </button>

          {showUrlField && (
            <div style={{ marginTop: '.4rem' }}>
              <input
                type="url"
                className="field-input"
                placeholder="https://yoursite.com/logo.png"
                value={config.branding.logo_url || ''}
                onChange={e =>
                  handleLogoUrl(e.target.value)
                }
                style={{ fontSize: '.88rem' }}
              />

              <p
                style={{
                  fontSize: '.7rem',
                  color: 'var(--dim)',
                  marginTop: '.3rem'
                }}
              >
                {t.s3_logo_hint}
              </p>
            </div>
          )}
        </div>

        {/* Colours */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '1rem'
          }}
        >
          <div>
            <label className="field-label">
              {t.s3_primary}
            </label>

            <div
              style={{
                display: 'flex',
                gap: '.5rem',
                alignItems: 'center'
              }}
            >
              <input
                type="color"
                value={config.branding.primary}
                onChange={e =>
                  upd('primary', e.target.value)
                }
                style={{
                  width: 42,
                  height: 38,
                  border: '1.5px solid var(--border2)',
                  borderRadius: 8,
                  padding: 2,
                  background: 'rgba(255,255,255,.05)',
                  cursor: 'pointer',
                  flexShrink: 0
                }}
              />

              <input
                type="text"
                className="field-input"
                value={config.branding.primary}
                onChange={e =>
                  upd('primary', e.target.value)
                }
                style={{
                  fontFamily: 'monospace',
                  fontSize: '.82rem'
                }}
              />
            </div>
          </div>

          <div>
            <label className="field-label">
              {t.s3_secondary}
            </label>

            <div
              style={{
                display: 'flex',
                gap: '.5rem',
                alignItems: 'center'
              }}
            >
              <input
                type="color"
                value={config.branding.secondary}
                onChange={e =>
                  upd('secondary', e.target.value)
                }
                style={{
                  width: 42,
                  height: 38,
                  border: '1.5px solid var(--border2)',
                  borderRadius: 8,
                  padding: 2,
                  background: 'rgba(255,255,255,.05)',
                  cursor: 'pointer',
                  flexShrink: 0
                }}
              />

              <input
                type="text"
                className="field-input"
                value={config.branding.secondary}
                onChange={e =>
                  upd('secondary', e.target.value)
                }
                style={{
                  fontFamily: 'monospace',
                  fontSize: '.82rem'
                }}
              />
            </div>
          </div>
        </div>

        {/* Continue */}
        <button
          type="button"
          className="btn btn-primary btn-lg btn-full"
          onClick={next}
          disabled={!ok}
        >
          {t.continue}
        </button>

        <StepNextHint step={3} />

      </div>
    </WizardShell>
  );
}